/**
 * Lead Tracking System API Types
 * Generated from api_contract.json - Phase 5
 *
 * These types match the backend Pydantic schemas exactly.
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * User role enumeration
 */
export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
}

/**
 * Lead status stages enumeration
 */
export enum LeadStatus {
  NEW = 'new',
  CONTACTED = 'contacted',
  ESTIMATE_SCHEDULED = 'estimate_scheduled',
  ESTIMATE_SENT = 'estimate_sent',
  WON = 'won',
  LOST = 'lost',
  QUALIFIED = 'qualified',
}

/**
 * Project status stages enumeration
 */
export enum ProjectStatus {
  NEW = 'new',
  ESTIMATE_SCHEDULED = 'estimate_scheduled',
  ESTIMATE_SENT = 'estimate_sent',
  WON = 'won',
  LOST = 'lost',
}

/**
 * Source channel enumeration
 */
export enum SourceChannel {
  PHONE = 'phone',
  WALK_IN = 'walk_in',
  WEBSITE = 'website',
  REFERRAL = 'referral',
  GOOGLE_ADS = 'google_ads',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  YELP = 'yelp',
  RECURRING_CLIENT = 'recurring_client',
  OTHER = 'other',
}

/**
 * Project size enumeration
 * tiny: <$1k, small: $1k-$4k, medium: $5k-$10k, big: +$10k-$20k, huge: +$20k
 */
export enum ProjectSize {
  TINY = 'tiny',
  SMALL = 'small',
  MEDIUM = 'medium',
  BIG = 'big',
  HUGE = 'huge',
}

/**
 * Next action enumeration
 */
export enum NextAction {
  CALL = 'call',
  EMAIL = 'email',
  SCHEDULE_ESTIMATE = 'schedule_estimate',
  SEND_QUOTE = 'send_quote',
  FOLLOW_UP = 'follow_up',
  OTHER = 'other',
}

/**
 * Activity log action types
 */
export enum ActivityAction {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  TRANSITION = 'transition',
}

/**
 * Entity types for activity logging
 */
export enum EntityType {
  LEAD = 'lead',
  CUSTOMER = 'customer',
  USER = 'user',
  PROJECT = 'project',
}

// ============================================================================
// AUTH TYPES
// ============================================================================

/**
 * Login request payload
 */
export interface LoginRequest {
  username: string;
  password: string;
}

/**
 * Login response payload
 */
export interface LoginResponse {
  message: string;
  user: UserMe;
  access_token: string;
}

// ============================================================================
// USER TYPES
// ============================================================================

/**
 * Current authenticated user information
 */
export interface UserMe {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
}

/**
 * User creation request
 */
export interface UserCreate {
  username: string;
  email: string;
  password: string;
  role?: UserRole;
}

/**
 * User update request
 */
export interface UserUpdate {
  username?: string;
  email?: string;
  role?: UserRole;
  is_active?: boolean;
}

/**
 * User response (without API key)
 */
export interface UserResponse {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Minimal user information for embedding in other responses
 */
export interface UserBasic {
  id: string;
  username: string;
}

// ============================================================================
// LEAD TYPES
// ============================================================================

/**
 * Lead creation request
 * Note: At least one of phone or email must be provided
 */
export interface LeadCreate {
  first_name: string;
  last_name: string;
  email?: string;
  email_other?: string;
  phone?: string;
  phone_other?: string;
  address?: string;
  city?: string;
  zip?: string;
  company_name?: string;
  source_channel: SourceChannel;
  source_campaign?: string;
  referrer_name?: string;
  referrer_url?: string;
  lead_score?: number;
  project_size?: ProjectSize;
  pain_points?: string;
  next_action?: NextAction;
  next_action_date?: string;
  notes?: string;
  assigned_to: string;
}

/**
 * Lead update request
 */
export interface LeadUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  email_other?: string;
  phone?: string;
  phone_other?: string;
  address?: string;
  city?: string;
  zip?: string;
  company_name?: string;
  source_channel?: SourceChannel;
  source_campaign?: string;
  referrer_name?: string;
  referrer_url?: string;
  lead_score?: number;
  project_size?: ProjectSize;
  pain_points?: string;
  assigned_to?: string;
  next_action?: NextAction;
  next_action_date?: string;
  notes?: string;
}

/**
 * Lead status transition request
 * Note: project_size required for 'won' status, disqualification_reason required for 'lost' status
 */
export interface LeadTransition {
  new_status: LeadStatus;
  project_size?: ProjectSize;
  disqualification_reason?: string;
}

/**
 * Lead response
 */
export interface LeadResponse {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  email_other?: string;
  phone?: string;
  phone_other?: string;
  address?: string;
  city?: string;
  zip?: string;
  company_name?: string;
  source_channel: SourceChannel;
  source_campaign?: string;
  referrer_name?: string;
  referrer_url?: string;
  lead_score?: number;
  lead_status: LeadStatus;
  project_size?: ProjectSize;
  pain_points?: string;
  assigned_to: string;
  assignee?: UserBasic;
  next_action?: NextAction;
  next_action_date?: string;
  last_contacted_at?: string;
  notes?: string;
  disqualification_reason?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Lead filter parameters
 */
export interface LeadFilterParams {
  lead_status?: LeadStatus;
  source_channel?: SourceChannel;
  project_size?: ProjectSize;
  assigned_to?: string;
  next_action?: NextAction;
  next_action_date_from?: string;
  next_action_date_to?: string;
  created_at_from?: string;
  created_at_to?: string;
  lead_score_min?: number;
  lead_score_max?: number;
  search?: string;
  sort_by?: 'created_at' | 'updated_at' | 'lead_score' | 'next_action_date' | 'last_name';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Paginated lead list response
 */
export interface LeadListResponse {
  items: LeadResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeadDuplicate {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}

export interface DuplicateCheckResponse {
  is_duplicate: boolean;
  lead?: LeadDuplicate;
  matched_fields: string[];
}

// ============================================================================
// PROJECT TYPES
// ============================================================================

/**
 * Project creation request
 */
export interface ProjectCreate {
  lead_id: string;
  address?: string;
  city?: string;
  zip?: string;
  project_size?: ProjectSize;
  project_cost?: number;
  estimate?: number;
  invoice?: number;
  pain_points?: string;
  next_action?: NextAction;
  next_action_date?: string;
  notes?: string;
}

/**
 * Project update request
 */
export interface ProjectUpdate {
  address?: string;
  city?: string;
  zip?: string;
  project_size?: ProjectSize;
  project_cost?: number;
  estimate?: number;
  invoice?: number;
  pain_points?: string;
  next_action?: NextAction;
  next_action_date?: string;
  notes?: string;
}

/**
 * Project status transition request
 */
export interface ProjectTransition {
  new_status: ProjectStatus;
  project_size?: ProjectSize;
  disqualification_reason?: string;
}

/**
 * Project response
 */
export interface ProjectResponse {
  id: string;
  lead_id: string;
  address?: string;
  city?: string;
  zip?: string;
  project_status: ProjectStatus;
  project_size?: ProjectSize;
  project_cost?: number;
  estimate?: number;
  invoice?: number;
  pain_points?: string;
  disqualification_reason?: string;
  next_action?: NextAction;
  next_action_date?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Project filter parameters
 */
export interface ProjectFilterParams {
  lead_id?: string;
  project_status?: ProjectStatus;
  project_size?: ProjectSize;
  next_action?: NextAction;
  next_action_date_from?: string;
  next_action_date_to?: string;
  created_at_from?: string;
  created_at_to?: string;
  search?: string;
  sort_by?: 'created_at' | 'updated_at' | 'next_action_date' | 'project_status';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Paginated project list response
 */
export interface ProjectListResponse {
  items: ProjectResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// WEB FORM TYPES
// ============================================================================

/**
 * Web form submission status
 */
export enum WebFormStatus {
  NEW = 'new',
  REVIEWED = 'reviewed',
  SPAM = 'spam',
  CONVERTED = 'converted',
}

/**
 * Web form response (contact form submissions)
 */
export interface LeadWebResponse {
  id: string;
  form_type: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email: string;
  phone?: string;
  city?: string;
  zip?: string;
  project?: string;
  sqft?: string;
  comment?: string;
  news_consent?: boolean;
  data_consent: boolean;
  ip_address?: string;
  status: string;
  converted_lead_id?: string;
  created_at: string;
}

/**
 * Paginated web form list response
 */
export interface LeadWebListResponse {
  items: LeadWebResponse[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Filter parameters for web form listing
 */
export interface LeadWebFilterParams {
  status?: WebFormStatus;
  form_type?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Response after converting a web form to a lead
 */
export interface ConvertToLeadResponse {
  lead_id: string;
  message: string;
}

// ============================================================================
// CUSTOMER TYPES
// ============================================================================

/**
 * Customer response
 */
export interface CustomerResponse {
  id: string;
  lead_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  zip?: string;
  company_name?: string;
  converted_at: string;
  referral_count: number;
}

/**
 * Customer filter parameters
 */
export interface CustomerFilterParams {
  converted_at_from?: string;
  converted_at_to?: string;
  search?: string;
  sort_by?: 'converted_at' | 'first_name' | 'last_name';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Paginated customer list response
 */
export interface CustomerListResponse {
  items: CustomerResponse[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Referral information for a lead referred by a customer
 */
export interface ReferralInfo {
  lead_id: string;
  lead_name: string;
  lead_status: string;
  created_at: string;
}

/**
 * Customer referral response
 */
export interface CustomerReferralResponse {
  customer_id: string;
  customer_name: string;
  referral_count: number;
  referrals: ReferralInfo[];
}

// ============================================================================
// STATISTICS TYPES
// ============================================================================

/**
 * Count by lead status
 */
export interface StatusCount {
  status: string;
  count: number;
}

/**
 * Count by source channel
 */
export interface SourceCount {
  source: string;
  count: number;
}

/**
 * Count by project size
 */
export interface SizeCount {
  size: string;
  count: number;
}

/**
 * Count by date
 */
export interface DateCount {
  date: string;
  count: number;
}

/**
 * Funnel statistics response
 */
export interface FunnelStats {
  count_by_status: StatusCount[];
  count_by_source: SourceCount[];
  count_by_size: SizeCount[];
  conversion_rate: number;
  avg_lead_score?: number;
  leads_by_date: DateCount[];
  total_leads: number;
  total_won: number;
  total_lost: number;
  count_by_project_status?: StatusCount[];
  total_projects?: number;
  total_projects_won?: number;
  total_projects_lost?: number;
}

/**
 * Funnel filter parameters
 */
export interface FunnelFilterParams {
  date_from?: string;
  date_to?: string;
  assigned_to?: string;
  group_by?: 'day' | 'week' | 'month';
}

// ============================================================================
// ACTIVITY LOG TYPES
// ============================================================================

/**
 * Activity log response
 */
export interface ActivityLogResponse {
  id: string;
  user_id: string;
  username?: string;
  action: ActivityAction;
  entity_type: EntityType;
  entity_id: string;
  description?: string;
  created_at: string;
}

/**
 * Activity log filter parameters
 */
export interface ActivityLogFilterParams {
  user_id?: string;
  entity_type?: EntityType;
  entity_id?: string;
  action?: ActivityAction;
  created_at_from?: string;
  created_at_to?: string;
  sort_by?: 'created_at' | 'user_id' | 'action';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Paginated activity log list response
 */
export interface ActivityLogListResponse {
  items: ActivityLogResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// STATUS TYPES
// ============================================================================

/**
 * Database connectivity status
 */
export interface DatabaseStatus {
  connected: boolean;
  latency_ms?: number;
  error?: string;
}

/**
 * System status response
 */
export interface StatusResponse {
  application: string;
  version: string;
  status: 'healthy' | 'degraded';
  timestamp: string;
  database: DatabaseStatus;
}

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * HTTP error response
 */
export interface HTTPError {
  detail: string;
}

/**
 * Validation error detail
 */
export interface ValidationErrorDetail {
  loc: string[];
  msg: string;
  type: string;
}

/**
 * Validation error response
 */
export interface ValidationError {
  detail: ValidationErrorDetail[];
}

// ============================================================================
// GENERIC TYPES
// ============================================================================

/**
 * Generic paginated response
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Generic pagination parameters
 */
export interface PaginationParams {
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// ============================================================================
// API CLIENT TYPES
// ============================================================================

/**
 * API request options
 */
export interface ApiRequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard for LeadStatus
 */
export function isLeadStatus(value: string): value is LeadStatus {
  return Object.values(LeadStatus).includes(value as LeadStatus);
}

/**
 * Type guard for SourceChannel
 */
export function isSourceChannel(value: string): value is SourceChannel {
  return Object.values(SourceChannel).includes(value as SourceChannel);
}

/**
 * Type guard for ProjectSize
 */
export function isProjectSize(value: string): value is ProjectSize {
  return Object.values(ProjectSize).includes(value as ProjectSize);
}

/**
 * Type guard for NextAction
 */
export function isNextAction(value: string): value is NextAction {
  return Object.values(NextAction).includes(value as NextAction);
}

/**
 * Type guard for UserRole
 */
export function isUserRole(value: string): value is UserRole {
  return Object.values(UserRole).includes(value as UserRole);
}

/**
 * Type guard for ActivityAction
 */
export function isActivityAction(value: string): value is ActivityAction {
  return Object.values(ActivityAction).includes(value as ActivityAction);
}

/**
 * Type guard for EntityType
 */
export function isEntityType(value: string): value is EntityType {
  return Object.values(EntityType).includes(value as EntityType);
}

/**
 * Type guard for ProjectStatus
 */
export function isProjectStatus(value: string): value is ProjectStatus {
  return Object.values(ProjectStatus).includes(value as ProjectStatus);
}

// ============================================================================
// JOIST SYNC
// ============================================================================

export type JoistTimeWindow = 'yesterday' | 'full' | 'custom';

export type JoistScanStatus = 'ready' | 'imported' | 'expired';

export type JoistPromotionStatus = 'pending' | 'promoted';

/** One preview row shown beneath the Scan button */
export interface JoistScanSampleRow {
  client_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  date_created: string;
}

export interface JoistScanResponse {
  scan_id: string;
  time_window: JoistTimeWindow;
  /** Resolved server-side - for 'yesterday' these are the concrete dates applied */
  start_date: string | null;
  end_date: string | null;
  estimates_filename: string | null;
  clients_filename: string | null;
  estimates_row_count: number;
  clients_row_count: number;
  total_rows: number;
  importable_rows: number;
  flagged_rows: number;
  unmatched_rows: number;
  status: JoistScanStatus;
  inserted_count: number | null;
  skipped_count: number | null;
  created_at: string;
  sample: JoistScanSampleRow[];
}

export interface JoistImportResponse {
  scan_id: string;
  inserted: number;
  skipped_existing: number;
  flagged: number;
  unmatched_skipped: number;
  created_ids: string[];
  message: string;
}

export interface JoistLeadResponse {
  id: string;
  joist_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  phone_other: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  source_channel: string;
  source_campaign: string | null;
  project_size: ProjectSize | null;
  assigned_to: string;
  last_contacted_at: string | null;
  notes: string | null;
  scan_id: string | null;
  promotion_status: JoistPromotionStatus;
  promoted_lead_id: string | null;
  promoted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JoistLeadListResponse {
  items: JoistLeadResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface JoistLeadFilterParams {
  promotion_status?: JoistPromotionStatus;
  scan_id?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** A Joist row that enriched an existing lead instead of creating a new one */
export interface JoistDuplicateLink {
  joist_lead_id: string;
  lead_id: string;
  client_name: string;
  /** Why the two were considered the same person: 'name' | 'email' | 'phone' */
  matched_on: string[];
  /** Lead columns that were blank and have now been populated from Joist */
  filled_fields: string[];
}

export interface JoistPromoteResponse {
  joist_lead_id: string;
  lead_id: string;
  /** True when an existing lead was enriched rather than a new one created */
  skipped_duplicate: boolean;
  /** Zero or one entry — same shape as the bulk response so the UI has one render path */
  duplicates: JoistDuplicateLink[];
  message: string;
}

export interface JoistPromoteError {
  joist_lead_id: string;
  client_name: string;
  reason: string;
}

export interface JoistPromoteAllResponse {
  promoted: number;
  skipped_duplicate: number;
  failed: number;
  lead_ids: string[];
  duplicates: JoistDuplicateLink[];
  errors: JoistPromoteError[];
  message: string;
}

/** Form payload for POST /joist-leads/scan */
export interface JoistScanRequest {
  estimatesFile: File;
  clientsFile: File;
  timeWindow: JoistTimeWindow;
  startDate?: string;
  endDate?: string;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Make specific properties required
 */
export type RequireFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

/**
 * Make specific properties optional
 */
export type OptionalFields<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Extract the items type from a paginated response
 */
export type PaginatedItems<T extends PaginatedResponse<unknown>> = T['items'][number];
